import {
  Avatar, Button, Card, DataTable, EmailInput, EmptyState, ErrorState, Field, Input, InvitationIssuedPanel, PageHeader,
  PhoneInput, Row, Select, Stack, StatusBadge, normalisePhone, useApi, useClampedPage, useI18n, useSession,
  type Column, type InvitationIssuedResponse, type InvitedPlatformUserResponse, type PlatformUserResponse,
} from '@daghfal/core';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';

/**
 * Platform staff administration (FR-PLT-06).
 *
 * These are Nasca-Sy's own employees. A platform membership grants nothing inside any tenant's data — that
 * still needs a support session the tenant approves — so what is decided here is who may operate the
 * platform and in which role.
 *
 * The server refuses three things outright and the screen says so rather than hiding the buttons: acting on
 * your own membership, removing the last active superadmin, and re-inviting somebody who already has
 * access. Hiding them would leave an administrator wondering why an action they can see does nothing.
 */
export function PlatformUsers() {
  const api = useApi();
  const queryClient = useQueryClient();
  const { t, formatDate } = useI18n();
  const { session } = useSession();

  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [inviting, setInviting] = useState(false);
  const [resent, setResent] = useState<InvitationIssuedResponse | null>(null);

  const users = useQuery({
    queryKey: ['platform', 'users', { search, status, page }],
    queryFn: () => api.platform.listUsers({ search, status, page, pageSize: 25 }),
    placeholderData: keepPreviousData,
  });
  useClampedPage(page, setPage, users.data?.totalPages);

  const roles = useQuery({
    queryKey: ['platform', 'roles'],
    queryFn: () => api.platform.listRoles(),
    // Roles are seeded with the product and do not change while the console is open.
    staleTime: Infinity,
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['platform', 'users'] });
  };

  const assignRole = useMutation({
    mutationFn: ({ membershipId, roleKey }: { membershipId: number; roleKey: string }) =>
      api.platform.assignRole(membershipId, roleKey),
    onSuccess: refresh,
  });

  const setEnabled = useMutation({
    mutationFn: ({ membershipId, enabled }: { membershipId: number; enabled: boolean }) =>
      enabled ? api.platform.enableUser(membershipId) : api.platform.disableUser(membershipId),
    onSuccess: refresh,
  });

  // A new link for someone who has not accepted. Every earlier link stops working (decision 2026-09-23 #16).
  const resend = useMutation({
    mutationFn: (membershipId: number) => api.platform.resendUserInvitation(membershipId),
    onSuccess: (invitation) => setResent(invitation),
  });

  const columns: Column<PlatformUserResponse>[] = [
    {
      key: 'name',
      header: t('users.column.name'),
      render: (user) => (
        <Row>
          <Avatar name={user.fullName} photoUrl={user.photoUrl} size="sm" />
          <Stack tight>
            <strong>{user.fullName ?? t('users.unnamed')}</strong>
            <span className="dg-muted" style={{ direction: 'ltr' }}>{user.phone}</span>
          </Stack>
        </Row>
      ),
    },
    { key: 'status', header: t('users.column.status'), render: (user) => <StatusBadge status={user.status} /> },
    {
      key: 'role',
      header: t('users.column.role'),
      render: (user) => (
        <Select
          value={user.roleKey}
          aria-label={t('users.column.role')}
          disabled={user.membershipId === session?.membership.membershipId || assignRole.isPending}
          onChange={(event) =>
            assignRole.mutate({ membershipId: user.membershipId, roleKey: event.target.value })}
        >
          {(roles.data ?? []).map((role) => (
            <option key={role.key} value={role.key}>{role.nameAr}</option>
          ))}
        </Select>
      ),
    },
    {
      key: 'lastLogin',
      header: t('users.column.lastLogin'),
      render: (user) => (user.lastLoginAt ? formatDate(user.lastLoginAt) : t('users.neverSignedIn')),
    },
    {
      key: 'actions',
      header: t('users.column.actions'),
      render: (user) => {
        // Your own row is left without an action. Withdrawing your own access is how an administrator
        // locks themselves out of the console that would let them undo it.
        if (user.membershipId === session?.membership.membershipId) {
          return <span className="dg-muted">{t('users.you')}</span>;
        }

        const disabled = user.status === 'Deactivated';
        return (
          <Row>
            {user.status === 'Invited' && (
              <Button
                variant="secondary"
                busy={resend.isPending && resend.variables === user.membershipId}
                onClick={() => { setResent(null); resend.mutate(user.membershipId); }}
              >
                {t('invitation.resend')}
              </Button>
            )}
            <Button
              variant={disabled ? 'secondary' : 'danger'}
              disabled={setEnabled.isPending}
              onClick={() => setEnabled.mutate({ membershipId: user.membershipId, enabled: disabled })}
            >
              {disabled ? t('users.enable') : t('users.disable')}
            </Button>
          </Row>
        );
      },
    },
  ];

  return (
    <>
      <PageHeader
        title={t('users.title')}
        lede={t('users.lede')}
        actions={
          <Button icon="＋" onClick={() => setInviting((open) => !open)}>
            {inviting ? t('action.cancel') : t('users.invite')}
          </Button>
        }
      />

      {inviting && (
        <InviteForm
          roles={(roles.data ?? []).map((role) => ({ key: role.key, name: role.nameAr }))}
          onInvited={() => { refresh(); setPage(1); }}
          onClose={() => setInviting(false)}
        />
      )}

      {resent && <InvitationIssuedPanel invitation={resent} onDismiss={() => setResent(null)} />}
      {resend.error != null && <ErrorState error={resend.error} />}
      {assignRole.error != null && <ErrorState error={assignRole.error} />}
      {setEnabled.error != null && <ErrorState error={setEnabled.error} />}

      <Card>
        <Row>
          <label className="dg-field" style={{ flex: '1 1 260px' }}>
            <span className="dg-visually-hidden">{t('users.search')}</span>
            <Input
              type="search"
              placeholder={t('users.search')}
              value={search}
              onChange={(event) => { setSearch(event.target.value); setPage(1); }}
            />
          </label>

          <label className="dg-field">
            <span className="dg-visually-hidden">{t('users.column.status')}</span>
            <Select
              value={status}
              onChange={(event) => { setStatus(event.target.value); setPage(1); }}
              style={{ inlineSize: 'auto' }}
            >
              <option value="">{t('users.status.all')}</option>
              <option value="Invited">{t('status.Invited')}</option>
              <option value="Active">{t('status.Active')}</option>
              <option value="Deactivated">{t('status.Deactivated')}</option>
            </Select>
          </label>
        </Row>
      </Card>

      <DataTable
        caption={t('users.caption')}
        columns={columns}
        rows={users.data?.items}
        rowKey={(user) => user.membershipId}
        isLoading={users.isPending}
        error={users.error}
        onRetry={() => void users.refetch()}
        empty={<EmptyState icon="👤" title={t('users.empty')} />}
      />

      {users.data && users.data.totalPages > 1 && (
        <Row between>
          <Button variant="secondary" disabled={page <= 1} onClick={() => setPage((n) => n - 1)}>
            {t('action.previous')}
          </Button>
          <span>{page} / {users.data.totalPages}</span>
          <Button
            variant="secondary"
            disabled={page >= users.data.totalPages}
            onClick={() => setPage((n) => n + 1)}
          >
            {t('action.next')}
          </Button>
        </Row>
      )}
    </>
  );
}

/**
 * The invitation form.
 *
 * The link goes to the invitee's phone by SMS and is also shown here once, to copy if the text does not
 * arrive. The platform never sets anyone's password; accepting needs a code sent to the invitee's phone.
 */
function InviteForm({ roles, onInvited, onClose }: {
  roles: { key: string; name: string }[];
  onInvited: () => void;
  onClose: () => void;
}) {
  const api = useApi();
  const { t } = useI18n();

  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [roleKey, setRoleKey] = useState('');
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [issued, setIssued] = useState<InvitedPlatformUserResponse | null>(null);

  const invite = useMutation({
    mutationFn: api.platform.inviteUser,
    onSuccess: (result) => { setIssued(result); onInvited(); },
  });

  function submit(event: FormEvent) {
    event.preventDefault();

    const normalised = normalisePhone(phone);
    if (!normalised) { setPhoneError(t('users.phone.invalid')); return; }
    setPhoneError(null);

    invite.mutate({
      fullName,
      phoneE164: normalised,
      email: email.trim() === '' ? null : email.trim(),
      roleKey: roleKey || (roles[0]?.key ?? ''),
    });
  }

  if (issued) {
    return (
      <Card title={t('users.invited.title')}>
        <Stack>
          <InvitationIssuedPanel invitation={issued.invitation} />
          <Row>
            <Button onClick={onClose}>{t('users.done')}</Button>
            <Button variant="secondary" onClick={() => { setIssued(null); setFullName(''); setPhone(''); setEmail(''); }}>
              {t('users.inviteAnother')}
            </Button>
          </Row>
        </Stack>
      </Card>
    );
  }

  return (
    <Card title={t('users.invite')} hint={t('users.invite.hint')}>
      <form onSubmit={submit}>
        <Stack>
          {invite.error != null && <ErrorState error={invite.error} />}

          <Field label={t('users.column.name')} required>
            {(props) => (
              <Input {...props} value={fullName} onChange={(event) => setFullName(event.target.value)} />
            )}
          </Field>

          <Field label={t('auth.phone')} hint={t('auth.phone.hint')} error={phoneError} required>
            {(props) => (
              <PhoneInput {...props} value={phone} onChange={(event) => setPhone(event.target.value)} />
            )}
          </Field>

          <Field label={t('invitation.email')} hint={t('invitation.email.hint')}>
            {(props) => (
              <EmailInput
                {...props}
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
            )}
          </Field>

          <Field label={t('users.column.role')} required>
            {(props) => (
              <Select {...props} value={roleKey} onChange={(event) => setRoleKey(event.target.value)}>
                <option value="">{t('users.role.choose')}</option>
                {roles.map((role) => <option key={role.key} value={role.key}>{role.name}</option>)}
              </Select>
            )}
          </Field>

          <Row>
            <Button type="submit" disabled={invite.isPending}>
              {invite.isPending ? t('action.saving') : t('users.invite.submit')}
            </Button>
            <Button type="button" variant="secondary" onClick={onClose}>{t('action.cancel')}</Button>
          </Row>
        </Stack>
      </form>
    </Card>
  );
}
